import React, { useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { MembersService } from '~/services/domain/MembersService';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useTranslation } from 'react-i18next';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { Button, ButtonText } from '@/components/gluestack/button';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { MemberDetails } from '~/models/MemberDetails';

export interface IComposeMessageProps extends IContextSheetContentProps {
  // member and onMessageSent are passed through payload
}

export function ComposeMessage({ payload }: IComposeMessageProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageText, setMessageText] = useState('');
  const inputRef = useDelayedAutoFocus();

  // Get member and callback from payload
  const member = payload?.member as MemberDetails;
  const handleMessageSent = payload?.onMessageSent;

  // Build a per-member storage key for draft
  const draftStorageKey = member ? `draft:compose:${member.Id}` : undefined;

  // Restore draft when member available/changes
  React.useEffect(() => {
    let isMounted = true;
    if (!draftStorageKey) return;
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(draftStorageKey);
        if (isMounted && stored) setMessageText(stored);
      } catch {}
    })();
    return () => {
      isMounted = false;
    };
  }, [draftStorageKey]);

  // Debounced persist of draft
  React.useEffect(() => {
    if (!draftStorageKey) return;
    const handle = setTimeout(() => {
      const trimmed = messageText.trim();
      if (trimmed.length > 0) {
        void AsyncStorage.setItem(draftStorageKey, messageText);
      } else {
        void AsyncStorage.removeItem(draftStorageKey);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [messageText, draftStorageKey]);

  const handleSubmit = async () => {
    if (!messageText.trim() || !member) return;

    setIsSubmitting(true);
    try {
      // Create a new message with this member
      const result = await MembersService.createNewMessage(member.Id, messageText.trim());
      
      // Show success message
      showSuccessToast(t('messages.success.messageSent'));
      
      // Call the callback with the message ID for navigation
      handleMessageSent?.(result?.Id);
      // Clear draft after successful send
      if (draftStorageKey) {
        await AsyncStorage.removeItem(draftStorageKey);
      }
      setMessageText('');
    } catch (error: any) {
      showErrorToast(t('messages.errors.failedToSend'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!member) {
    return null;
  }

  return (
    <VStack className="flex-1 gap-4">
      <Textarea className="flex-1">
        <TextareaInput
          ref={inputRef}
          placeholder={t('messages.compose.enterMessage')}
          value={messageText}
          onChangeText={setMessageText}
          textAlignVertical="top"
          className="m-0 text-base"
        />
      </Textarea>

      <HStack className="shrink-0 justify-end">
        <Button
          onPress={handleSubmit}
          disabled={isSubmitting || !messageText.trim()}
          className="self-end disabled:bg-primary-200"
          action="primary"
          size="md">
          <ButtonText className={isSubmitting || !messageText.trim() ? 'text-white/50' : ''}>
            {t('common.send')}
          </ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}

ComposeMessage.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'md',
  adjustForWebKeyboard: true,
} satisfies ISheetConfig;